import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {MCPOAuthApi, ProjectPermission} from "../../../api/authority/generated/index.ts";
import Button from "../../../components/Button.jsx";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {hasSubscriptionFeature} from "../../../utils/subscriptionFeatures.js";

export async function loadProjectMcpGrants(api, projectId) {
    const grants = await api.listProjectMCPGrants({projectId});
    return grants.filter((grant) => grant.projectId === projectId);
}

export async function loadProjectMcpConnections(api, projectId) {
    const [metadata, grants] = await Promise.all([
        api.mCPProtectedResourceMetadata(),
        loadProjectMcpGrants(api, projectId),
    ]);

    return {resource: metadata.resource, grants};
}

export function hasVisibleProjectMcpAccess(project, hasProjectAccess, now = new Date()) {
    const subscription = project?.subscription;
    const packageCode = subscription?.currentPackage?.code;
    const endTimestamp = typeof subscription?.endDate === "string"
        ? Date.parse(subscription.endDate)
        : Number.NaN;
    const nowTimestamp = now instanceof Date ? now.getTime() : Number.NaN;
    const applicablePrice = getProjectMcpApplicablePrice(project);
    return hasProjectAccess === true
        && subscription?.status === "active"
        && subscription?.isFreeTrial === false
        && subscription?.cancelAtPeriodEnd === false
        && Number.isFinite(endTimestamp)
        && Number.isFinite(nowTimestamp)
        && endTimestamp > nowTimestamp
        && typeof packageCode === "string"
        && packageCode !== "free"
        && Number.isFinite(applicablePrice)
        && applicablePrice > 0
        && hasSubscriptionFeature(project, "mcp_access");
}

export function getProjectMcpApplicablePrice(project) {
    const subscription = project?.subscription;
    const currentPackage = subscription?.currentPackage;
    const priceField = {
        'CZK:monthly': 'monthlyCzk',
        'CZK:annual': 'yearlyCzk',
        'EUR:monthly': 'monthlyEur',
        'EUR:annual': 'yearlyEur',
    }[`${project?.currency}:${subscription?.billingCycle}`];

    if (priceField === undefined) return Number.NaN;
    const price = currentPackage?.[priceField];
    return typeof price === 'number' ? price : Number.NaN;
}

export function getProjectSettingsAccess(project, hasPermission, isProjectMember) {
    return {
        canEditProfile: hasPermission(ProjectPermission.ProjectProfile, project),
        canViewBilling: hasPermission(ProjectPermission.ProjectBilling, project),
        canViewMcp: hasVisibleProjectMcpAccess(project, isProjectMember),
    };
}

export async function revokeAndReloadProjectMcpGrant(api, projectId, grantId) {
    await api.revokeProjectMCPGrant({projectId, grantId});
    return loadProjectMcpGrants(api, projectId);
}

export function ProjectMcpGrantList({
    grants,
    confirmingGrantId,
    revokingGrantId,
    onStartRevoke,
    onCancelRevoke,
    onConfirmRevoke,
    t,
}) {
    const confirmButtonRef = useRef(null);
    const revokeButtonRefs = useRef(new Map());
    const returnFocusGrantIdRef = useRef(null);
    useEffect(() => {
        if (confirmingGrantId !== null) {
            confirmButtonRef.current?.focus();
        } else if (returnFocusGrantIdRef.current !== null) {
            revokeButtonRefs.current.get(returnFocusGrantIdRef.current)?.focus();
            returnFocusGrantIdRef.current = null;
        }
    }, [confirmingGrantId]);

    if (grants.length === 0) {
        return <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("project.mcp.empty", {defaultValue: "K tomuto projektu není připojen žádný AI klient."})}
        </p>;
    }

    return <div className="space-y-3">
        {grants.map((grant) => {
            const isConfirming = confirmingGrantId === grant.grantId;
            const isRevoking = revokingGrantId === grant.grantId;

            return <article key={grant.grantId} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{grant.clientName}</h3>
                        <dl className="mt-2 grid gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex flex-wrap gap-1">
                                <dt>{t("project.mcp.client-id", {defaultValue: "ID klienta"})}:</dt>
                                <dd className="break-all font-mono">{grant.clientId}</dd>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                <dt>{t("project.mcp.resource", {defaultValue: "MCP zdroj"})}:</dt>
                                <dd className="break-all font-mono">{grant.resource}</dd>
                            </div>
                        </dl>
                    </div>

                    {!isConfirming && <Button
                        ref={(node) => {
                            if (node) revokeButtonRefs.current.set(grant.grantId, node);
                            else revokeButtonRefs.current.delete(grant.grantId);
                        }}
                        variant="outline-red"
                        size="small"
                        disabled={revokingGrantId !== null}
                        onClick={() => {
                            returnFocusGrantIdRef.current = grant.grantId;
                            onStartRevoke(grant.grantId);
                        }}
                        aria-label={t("project.mcp.revoke-client", {
                            defaultValue: "Odebrat přístup klientovi {{clientName}}",
                            clientName: grant.clientName,
                        })}
                    >
                        {t("project.mcp.revoke", {defaultValue: "Odebrat přístup"})}
                    </Button>}
                </div>

                {isConfirming && <div role="group" aria-label={t("project.mcp.confirmation", {defaultValue: "Potvrzení odebrání přístupu"})} className="mt-4 rounded-lg bg-rose-50 p-3 dark:bg-rose-950/30">
                    <p className="text-sm text-rose-800 dark:text-rose-200">
                        {t("project.mcp.revoke-warning", {
                            defaultValue: "Klient {{clientName}} ztratí přístup k tomuto projektu. Data, která už externí služba zkopírovala, tím nebudou smazána.",
                            clientName: grant.clientName,
                        })}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button ref={isConfirming ? confirmButtonRef : null} variant="outline-red" size="small" disabled={isRevoking} onClick={() => onConfirmRevoke(grant.grantId)}>
                            {isRevoking
                                ? t("project.mcp.revoking", {defaultValue: "Odebírám…"})
                                : t("project.mcp.confirm-revoke", {defaultValue: "Ano, odebrat přístup"})}
                        </Button>
                        <Button variant="outline-primary" size="small" disabled={isRevoking} onClick={onCancelRevoke}>
                            {t("project.mcp.cancel", {defaultValue: "Zrušit"})}
                        </Button>
                    </div>
                </div>}
            </article>;
        })}
    </div>;
}

export function ProjectMcpGrantsPanel({
    resource,
    grants,
    error,
    confirmingGrantId,
    revokingGrantId,
    onRefresh,
    onStartRevoke,
    onCancelRevoke,
    onConfirmRevoke,
    onCopyResource,
    liveMessage,
    t,
}) {
    return <section aria-label={t("project.mcp.box-title", {defaultValue: "MCP pro AI asistenty"})}>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
                <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                    {t("project.mcp.description", {defaultValue: "Připojte AI asistenta přes zabezpečené OAuth přihlášení. Každý uživatel povoluje přístup sám za sebe a pouze k tomuto projektu."})}
                </p>
            </div>
            <Button variant="outline-primary" size="small" disabled={grants === null || revokingGrantId !== null} onClick={onRefresh}>
                {t("project.mcp.refresh", {defaultValue: "Aktualizovat"})}
            </Button>
        </div>

        {resource && <div className="mb-5">
            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t("project.mcp.url", {defaultValue: "MCP URL"})}
            </div>
            <div className="mt-2 flex flex-col items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900 sm:flex-row">
                <span className="min-w-0 flex-1 break-all font-mono text-sm text-gray-800 dark:text-gray-200">{resource}</span>
                <Button variant="outline-primary" size="small" className="shrink-0" onClick={onCopyResource} aria-label={t('copy-to-clipboard')}>
                    {t('copy', {defaultValue: 'Kopírovat'})}
                </Button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t("project.mcp.url-help", {defaultValue: "Tuto adresu vložte do svého MCP klienta. Přihlášení vás následně přesměruje do Datixo, kde výslovně vyberete jeden projekt."})}
            </p>
        </div>}

        {grants !== null && <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t("project.mcp.connections", {defaultValue: "Povolená připojení"})}
        </h3>}

        {grants === null && !error && <p role="status" className="text-sm text-gray-500 dark:text-gray-400">
            {t("project.mcp.loading", {defaultValue: "Načítám MCP připojení…"})}
        </p>}

        {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
            <p>{t("project.mcp.error", {defaultValue: "MCP připojení se nepodařilo načíst nebo změnit."})}</p>
            <Button variant="outline-red" size="small" className="mt-3" disabled={revokingGrantId !== null} onClick={onRefresh}>
                {t("project.mcp.retry", {defaultValue: "Zkusit znovu"})}
            </Button>
        </div>}

        {grants !== null && <ProjectMcpGrantList
            grants={grants}
            confirmingGrantId={confirmingGrantId}
            revokingGrantId={revokingGrantId}
            onStartRevoke={onStartRevoke}
            onCancelRevoke={onCancelRevoke}
            onConfirmRevoke={onConfirmRevoke}
            t={t}
        />}
        <p className="sr-only" role="status" aria-live="polite">{liveMessage}</p>
    </section>;
}

export function EntitledProjectMcpGrants({project}) {
    const {t} = useTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const apiRef = useRef(null);
    const currentProjectIdRef = useRef(project.id);
    const [grants, setGrants] = useState(null);
    const [resource, setResource] = useState(null);
    const [error, setError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmingGrantId, setConfirmingGrantId] = useState(null);
    const [revokingGrantId, setRevokingGrantId] = useState(null);
    const [liveMessage, setLiveMessage] = useState('');

    currentProjectIdRef.current = project.id;
    if (apiRef.current === null) {
        apiRef.current = new MCPOAuthApi(getAuthorityConfiguration());
    }

    useEffect(() => {
        let active = true;
        const requestedProjectId = project.id;

        setGrants(null);
        setResource(null);
        setError(false);
        setConfirmingGrantId(null);
        setRevokingGrantId(null);
        setLiveMessage('');

        loadProjectMcpConnections(apiRef.current, requestedProjectId)
            .then((result) => {
                if (active && currentProjectIdRef.current === requestedProjectId) {
                    setResource(result.resource);
                    setGrants(result.grants);
                }
            })
            .catch(() => {
                if (active && currentProjectIdRef.current === requestedProjectId) setError(true);
            });

        return () => { active = false; };
    }, [project.id, refreshKey]);

    const revokeGrant = async (grantId) => {
        const requestedProjectId = project.id;
        setRevokingGrantId(grantId);
        setError(false);

        try {
            const result = await revokeAndReloadProjectMcpGrant(apiRef.current, requestedProjectId, grantId);
            if (currentProjectIdRef.current === requestedProjectId) {
                setGrants(result);
                setConfirmingGrantId(null);
                setLiveMessage(t('project.mcp.revoke-success', {defaultValue: 'Přístup klienta byl odebrán.'}));
            }
        } catch {
            if (currentProjectIdRef.current === requestedProjectId) setError(true);
        } finally {
            if (currentProjectIdRef.current === requestedProjectId) setRevokingGrantId(null);
        }
    };

    return <ProjectMcpGrantsPanel
        resource={resource}
        grants={grants}
        error={error}
        confirmingGrantId={confirmingGrantId}
        revokingGrantId={revokingGrantId}
        onRefresh={() => setRefreshKey((key) => key + 1)}
        onStartRevoke={setConfirmingGrantId}
        onCancelRevoke={() => setConfirmingGrantId(null)}
        onConfirmRevoke={revokeGrant}
        onCopyResource={() => Promise.resolve()
            .then(() => navigator.clipboard.writeText(resource))
            .then(() => toast.success(t('copy-to-clipboard-success')))
            .catch(() => toast.error(t('copy-to-clipboard-failed')))}
        liveMessage={liveMessage}
        t={t}
    />;
}

export default function ProjectMcpGrants({project}) {
    if (!hasVisibleProjectMcpAccess(project, true)) {
        return null;
    }

    return <EntitledProjectMcpGrants key={project.id} project={project}/>;
}
